import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import SchemaQuestionCard from '../components/SchemaQuestionCard';
import FilterBanner from '../components/FilterBanner';
import { useHGS } from '../context/HGSContext';
import { MODULE_SCHEMAS } from '../data/masterData';
import { DLC_INSIGHTS } from '../data/mockData';
import { ArchitectureProgress } from './RiskUniversePage';
import { filterRowsForUser, canFillModuleForms } from '../data/rbac';
import { computeDlcAutofill, autoPopStats } from '../data/dlcAutofill';
import RiskUnpublishedGate from '../components/RiskUnpublishedGate';
import MasterEmptyState from '../components/MasterEmptyState';
import { FileSignature, AlertTriangle, ClipboardCheck, ShieldCheck, Send, Code2, Sparkles, Filter, Zap, Wand2, FileCheck2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

const ANSWERABLE = ['responseType', 'deficiency', 'currentSituation', 'evidenceRequired', 'evidenceFile'];

export default function DLCPage() {
  const { masters, masterMeta, user, formResponses, riskPublished } = useHGS();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const schema = MODULE_SCHEMAS.dlc;
  const meta = masterMeta.dlc;
  const allRows = useMemo(() => filterRowsForUser(user, (masters.dlc || []).filter((r) => r.active !== false)), [masters, user]);
  const canEdit = canFillModuleForms(user);
  const masterUploaded = (masters.dlc || []).length > 0;

  const today = useMemo(() => new Date(), []);

  // Real auto-population: derive from submitted Fraud + PCC responses by department
  const autofill = useMemo(() => computeDlcAutofill({
    dlcRows: allRows,
    fraudRows: filterRowsForUser(user, (masters.fraud || []).filter((r) => r.active !== false)),
    pccRows: filterRowsForUser(user, (masters.pcc || []).filter((r) => r.active !== false)),
    formResponses,
  }), [allRows, masters, formResponses, user]);

  const autoPopRate = useMemo(() => autoPopStats(autofill), [autofill]);
  const rows = useMemo(() => {
    if (!filter) return allRows;
    return allRows.filter((r) => {
      const resp = formResponses[`dlc:${r.questionId}`];
      const v = resp?.values || {};
      switch (filter) {
        case 'overdue': {
          const etc = v.expectedCompletionDate || r.expectedCompletionDate;
          if (!etc) return false;
          const dt = new Date(etc);
          return !Number.isNaN(dt.getTime()) && dt < today;
        }
        case 'no-or-na':
          return v.responseType === 'No' || v.responseType === 'N/A';
        case 'pending':
          return !resp;
        case 'missing-evidence':
          return v.evidenceRequired === 'Yes' && !v.evidenceFile;
        default:
          return true;
      }
    });
  }, [allRows, filter, formResponses, today]);

  return (
    <div>
      <TopBar
        title="DLC Self-Verification"
        subtitle="Step 4 of 5 · Auto-populated from Fraud + PCC · Division head attests to outstanding items"
        breadcrumbs={['HGS Architecture', 'DLC Self-Verification']}
        actions={
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-sm font-semibold rounded-lg shadow-sm transition">
            <Send className="w-4 h-4" />
            Submit Declaration
          </button>
        }
      />

      <div className="p-8 space-y-5">
        <ArchitectureProgress step={4} />

        {!riskPublished?.published && <RiskUnpublishedGate user={user} />}

        {riskPublished?.published && !canEdit && (
          <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid="review-mode-banner">
            <Lock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-600 leading-relaxed">
              <span className="font-bold text-slate-900">Review-only mode</span> — DLC declarations are filled by Department Heads. Use Approvals to review their submissions.
            </div>
          </div>
        )}

        {riskPublished?.published && !masterUploaded && (
          <MasterEmptyState moduleLabel="DLC Self-Verification" moduleKey="dlc" accent="#7C3AED" isAdmin={user?.role === 'admin'} />
        )}

        {riskPublished?.published && masterUploaded && (<>
        {/* Auto-pop KPI strip */}
        <div className="bg-gradient-to-r from-violet-50 via-white to-slate-50 rounded-xl border border-violet-200 p-5" data-testid="dlc-autopop-kpi">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-[#7C3AED] flex items-center justify-center text-white">
              <Zap className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-bold text-violet-700 uppercase tracking-wider">Auto-population Rate</div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-slate-900 tabular-nums">{autoPopRate.rate}%</span>
                <span className="text-xs text-slate-500">{autoPopRate.auto} of {autoPopRate.total} declarations pre-filled from Fraud + PCC</span>
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-500 to-[#7C3AED] rounded-full transition-all" style={{ width: `${autoPopRate.rate}%` }} />
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Target</div>
              <div className="text-sm font-bold text-slate-900">60–70%</div>
              <div className={cn('text-[10px] font-bold mt-0.5',
                autoPopRate.rate >= 60 && autoPopRate.rate <= 75 ? 'text-emerald-600' : 'text-amber-600'
              )}>
                {autoPopRate.rate >= 60 && autoPopRate.rate <= 75 ? '✓ Within target' : 'Needs attention'}
              </div>
            </div>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Auto-pulled insights */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#CC0000]" />
              <h3 className="text-sm font-bold text-slate-900">Unresolved Risks</h3>
            </div>
            <div className="space-y-2">
              {DLC_INSIGHTS.unresolvedRisks.map((r) => (
                <div key={r.id} className="p-3 rounded-lg bg-red-50/50 border border-red-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-[10px] text-slate-500">{r.id}</span>
                    <StatusBadge status={r.score} />
                  </div>
                  <div className="text-xs font-semibold text-slate-900">{r.title}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">Open for {r.age} days</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-bold text-slate-900">PCC Pull-through</h3>
            </div>
            <div className="space-y-2">
              {DLC_INSIGHTS.pccPullthrough.map((p) => (
                <div key={`pcc-pt-${p.item}`} className="flex items-start justify-between gap-2 p-2.5 rounded-lg bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-slate-900">{p.item}</div>
                    <div className="text-[10px] text-slate-500">{p.section}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-bold text-slate-900">Fraud Pull-through</h3>
            </div>
            <div className="space-y-2">
              {DLC_INSIGHTS.fraudPullthrough.map((p) => (
                <div key={`fraud-pt-${p.item}`} className="flex items-start justify-between gap-2 p-2.5 rounded-lg bg-slate-50">
                  <div className="text-xs font-medium text-slate-900">{p.item}</div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Declaration form (schema-driven) */}
        <div className="lg:col-span-2 space-y-4">
          {filter && (
            <FilterBanner activeFilter={filter} count={rows.length} totalCount={allRows.length} />
          )}

          <div className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 to-[#1a0033] text-white">
            <Sparkles className="w-4 h-4 text-violet-300 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold flex items-center gap-2 flex-wrap">
                Declarations rendered live from
                <span className="font-mono px-2 py-0.5 rounded bg-white/10 text-violet-300">{schema.label}</span>
                <span className="font-mono px-2 py-0.5 rounded bg-white/10">{meta?.version || 'v1.0'}</span>
                <StatusBadge status={meta?.status || 'Active'} dot={false} />
              </div>
              <div className="text-[11px] text-slate-300 mt-1">
                {rows.length} declarations · last published on {meta?.lastUploadedAt || '—'}
              </div>
            </div>
            <Code2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#CC0000] to-[#990000] flex items-center justify-center">
                <FileSignature className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Quarterly Declaration</h3>
                <p className="text-xs text-slate-500">Respond to each declaration · No / N/A reveals Current Situation, Countermeasure & ETC fields</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {filter && rows.length === 0 && (
                <div className="p-8 text-center bg-slate-50 rounded-lg border border-slate-200">
                  <Filter className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm font-semibold text-slate-700">No declarations match this filter</p>
                </div>
              )}
              {rows.map((row, i) => {
                const af = autofill[row[schema.idField]];
                const prefill = af?.canAutoPopulate && af?.suggestedResponse
                  ? { responseType: af.suggestedResponse }
                  : {};
                return (
                  <div key={row[schema.idField]} data-testid={`dlc-row-${row[schema.idField]}`}>
                    {af?.canAutoPopulate && (
                      <div className="flex flex-col gap-1.5 px-3 py-2 rounded-t-lg bg-gradient-to-r from-violet-50 to-emerald-50 border border-b-0 border-violet-200 -mb-1" data-testid={`autofill-${row[schema.idField]}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="w-5 h-5 rounded-md bg-violet-500 text-white flex items-center justify-center flex-shrink-0">
                            <Wand2 className="w-3 h-3" />
                          </div>
                          <span className="text-[10px] font-bold text-violet-900">Auto-populated: Yes</span>
                          <span className="text-[10px] text-violet-700">· derived from {af.sources.length} upstream {af.sources.length === 1 ? 'response' : 'responses'}:</span>
                          {af.sources.slice(0, 5).map((s) => (
                            <span key={s.key} className="font-mono text-[9px] px-1 py-0.5 rounded bg-white text-violet-700 font-bold">{s.id}</span>
                          ))}
                        </div>
                        {af.evidenceRefs && af.evidenceRefs.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap pl-7" data-testid={`evidence-refs-${row[schema.idField]}`}>
                            <span className="text-[9px] font-bold text-emerald-800 uppercase tracking-wider">Evidence inherited:</span>
                            {af.evidenceRefs.slice(0, 6).map((e) => (
                              <span key={e.key} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 border border-emerald-200 text-emerald-900 text-[9px] font-bold">
                                <FileCheck2 className="w-2.5 h-2.5" />
                                {e.evidenceFile ? e.evidenceFile : `${e.id}-evidence.pdf`}
                              </span>
                            ))}
                            <span className="text-[9px] text-emerald-700 italic">· no separate DLC upload needed</span>
                          </div>
                        )}
                      </div>
                    )}
                    {af && !af.canAutoPopulate && af.needsManual && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-t-lg bg-amber-50 border border-b-0 border-amber-200 -mb-1" data-testid={`manual-required-${row[schema.idField]}`}>
                        <AlertTriangle className="w-3 h-3 text-amber-700 flex-shrink-0" />
                        <div className="text-[10px] text-amber-900 leading-snug">
                          <span className="font-bold">Manual answer required</span>
                          <span className="text-amber-700"> · {af.sources.filter((s) => ['No', 'N/A', 'NG'].includes(s.responseType)).length} upstream response(s) were No/NA. Division Head must review before signing.</span>
                        </div>
                      </div>
                    )}
                    <SchemaQuestionCard
                      schema={schema}
                      questionRow={row}
                      index={i}
                      questionTextKey="declarationQuestion"
                      answerableKeys={ANSWERABLE}
                      accent="#7C3AED"
                      initialValues={prefill}
                      readOnly={!canEdit}
                    />
                  </div>
                );
              })}
            </div>
            <div className="p-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-slate-600 leading-relaxed max-w-xl">
                I, <span className="font-bold text-slate-900">{user?.name || 'Rajesh Kumar'} (Division Head, Manufacturing)</span>, attest that the responses above are true and complete to the best of my knowledge as of {new Date().toLocaleDateString()}.
              </div>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-sm font-semibold rounded-lg shadow-sm">
                <Send className="w-4 h-4" />
                Submit Declaration
              </button>
            </div>
          </div>
        </div>
      </div>
        </>)}
      </div>
    </div>
  );
}
